import React, { Component } from "react";
import { can } from '../permissions/access';
import TopHeader from "../template/TopHeader";
import { connect } from "react-redux";
import ClientSidebar from "./ClientSidebar";
import axios from 'axios';
import API from "../../config/API";
import Lottie from "react-lottie";
import animationData from "../../assets/animation-data/loader.json";
import { Modal } from "react-bootstrap";
import ImportSection from "../onboarding/ImportSection";
import "./Clients.css";

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

class ExportImport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      exporting: false,
      showExportModal: false,
      exportFormat: 'csv',
      exportOptions: {
        includeOwn: true,
        includeShared: false,
        includeSalon: false,
      },
      isSelfEmployed: false,
      isSalonOwner: false,
    };
  }

  componentDidMount() {
    this.checkUserRole();
  }

  checkUserRole = () => {
    const user = this.props.authentication?.user || JSON.parse(localStorage.getItem('user'));
    const hairdresser = user?.hairdresser;
    
    this.setState({
      isSelfEmployed: hairdresser?.employmentType === 'self-employed',
      isSalonOwner: hairdresser?.isSalonOwner === 1,
    });
  };

  handleExportClick = () => {
    this.setState({ showExportModal: true });
  };

  handleCloseExportModal = () => {
    this.setState({ showExportModal: false });
  };

  handleExportOptionChange = (option) => {
    this.setState(prevState => ({
      exportOptions: {
        ...prevState.exportOptions,
        [option]: !prevState.exportOptions[option],
      },
    }));
  };

  handleExport = async () => {
    try {
      this.setState({ exporting: true });

      const { exportFormat } = this.state;
      
      const response = await axios.get(`${API.HAIRDRESSER_CLIENTS_EXPORT}?format=${exportFormat}`, {
        headers: {
          'Content-Type': 'application/json',
          'accessToken': localStorage.getItem('accessToken'),
        },
        responseType: exportFormat === 'csv' ? 'blob' : 'json'
      });

      if (exportFormat === 'csv') {
        // Create download link for CSV
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      alert('Clients exported successfully');
      this.setState({
        exporting: false,
        showExportModal: false,
      });
    } catch (error) {
      console.error('Export error:', error);
      alert(error.response?.data?.message || 'Failed to export clients');
      this.setState({ exporting: false });
    }
  };

  render() {
    const { 
      loading, 
      exporting, 
      showExportModal, 
      exportFormat,
      exportOptions,
      isSelfEmployed,
      isSalonOwner,
    } = this.state;

    // Roles & permissions: or anyone whose role has clients_export.
    const canExport = isSelfEmployed || isSalonOwner || can('clients_export');

    return (
      <div className="h_n">
        <div className="body_container cm_bg_white">
          <TopHeader title="Clients" headerButtonStatus={true} {...this.props} />
          <div className="new-setting-wrapper">
            <div className="container">
              <div className="row">
                <div className="col-md-4 col-sm-12 mobile-hidden">
                  <ClientSidebar />
                </div>
                <div className="col-md-8">
                  <div className="clients-table-container clients-page-padded">
                    <div className="export-import-header">
                      <h2>Export / Import Clients</h2>
                      <p className="text-muted">
                        Export your client data or import clients from another system
                      </p>
                    </div>

                    {!canExport ? (
                      <div className="alert alert-info mt-4">
                        <h4>Not Available</h4>
                        <p>
                          Export functionality is only available for self-employed hairdressers and salon owners.
                          As an employed staff member, your client data is managed by the salon.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Export Section */}
                        <div className="export-section mt-4">
                          <div className="section-card">
                            <div className="section-header">
                              <div>
                                <h4>Export Client Data</h4>
                                <p className="text-muted mb-0">
                                  Download your client information in CSV or JSON format
                                </p>
                              </div>
                              <button
                                className="btn btn-primary"
                                onClick={this.handleExportClick}
                                disabled={exporting}
                              >
                                <i className="fa fa-download mr-2"></i>
                                Export Clients
                              </button>
                            </div>

                            <div className="export-info mt-3">
                              <h5>What's included in the export:</h5>
                              <ul>
                                <li>Client names and contact information</li>
                                <li>Booking history and preferences</li>
                                <li>Notes and custom fields</li>
                                <li>Sales and visit statistics</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Import: the AI import layer (ONBOARDING-IMPORT-PLAN.md §4.5) + history */}
                        <ImportSection />

                        {/* Data Portability Info */}
                        <div className="data-portability-info mt-4 p-3 bg-light rounded">
                          <h5>Data Portability</h5>
                          <p>
                            As a {isSelfEmployed ? 'self-employed hairdresser' : 'salon owner'}, 
                            you have the right to export your client data at any time. This ensures 
                            you can take your client relationships with you if you move to a different 
                            salon or system.
                          </p>
                          {isSelfEmployed && (
                            <p className="mb-0">
                              <strong>Note:</strong> Only clients you own (including those you've shared 
                              with the salon) will be included in your export. Clients owned by other 
                              team members are not included.
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Export Modal */}
        <Modal show={showExportModal} onHide={this.handleCloseExportModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Export Client Data</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="export-modal-content">
              <div className="form-group">
                <label>Export Format</label>
                <select
                  className="form-control"
                  value={exportFormat}
                  onChange={(e) => this.setState({ exportFormat: e.target.value })}
                >
                  <option value="csv">CSV (Excel compatible)</option>
                  <option value="json">JSON (for developers)</option>
                </select>
              </div>

              <div className="alert alert-info mt-3">
                <strong>Note:</strong> This will export all clients you have access to based on your role and sharing settings.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={this.handleCloseExportModal}
              disabled={exporting}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={this.handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fa fa-download mr-2"></i>
                  Export
                </>
              )}
            </button>
          </Modal.Footer>
        </Modal>

      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    ...state,
  };
}

export default connect(mapStateToProps)(ExportImport);